"use client"

import { Treemap as RTreemap, Tooltip, ResponsiveContainer } from "recharts"
import { DEFAULT_PALETTE } from "./types"
import type { ChartDimension } from "./types"
import type { CSSProperties } from "react"

export interface TreemapNode {
  name: string
  size?: number
  children?: TreemapNode[]
  fill?: string
}

export interface TreemapChartProps {
  data: TreemapNode[]
  palette?: string[]
  /** Field with the leaf-node weight (defaults to "size"). */
  dataKey?: string
  showTooltip?: boolean
  className?: string
  style?: CSSProperties
  height?: ChartDimension
  width?: ChartDimension
  aspectRatio?: number
}

export function TreemapChart({
  data, palette = DEFAULT_PALETTE, dataKey = "size", showTooltip = true,
  className, style, height = "100%", width = "100%", aspectRatio = 4 / 3,
}: TreemapChartProps) {
  // Recharts Treemap takes a single root node. Wrap a flat list under one
  // top-level node and assign palette colors per leaf.
  const decorated = data.map((d, i) => ({ ...d, fill: d.fill || palette[i % palette.length] }))
  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <RTreemap
        // recharts' TreemapDataType uses an `unknown` index signature; the
        // declared shape is stricter than what we receive here, so we cast.
        data={decorated as unknown as Parameters<typeof RTreemap>[0]["data"]}
        dataKey={dataKey}
        aspectRatio={aspectRatio}
        stroke="hsl(var(--card))"
        fill={palette[0]}
        isAnimationActive={false}
      >
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
      </RTreemap>
    </ResponsiveContainer>
  )
}
